import { type FC, useCallback, useState } from "react";

import { AutoSizer, SearchableCard, VirtualTable } from "@snapchat/snap-design-system";

import { type LensSpace } from "../../gql-generated/types";
import {
    LensFoldersSearchParams,
    useSelectedLensCentralOrganizationNotIncludesCamkitOrgs,
    useTableFilterAndSort,
} from "../../shared/hooks";

import { rowCSS } from "./LensFolderList.styles";
import { getColumns } from "./LensFolderListColumns";
import { fullHeightCardCSS } from "./LensList.styles";
import { useNativeSearchParams } from "./useNativeSearchParams";

interface LensFolderListProps {
    handleView: (view: "folder" | "lens") => void;
}

export const LensFolderList: FC<LensFolderListProps> = ({ handleView }) => {
    const selectedOrganizationV3 = useSelectedLensCentralOrganizationNotIncludesCamkitOrgs();
    const { setNativeSearchParams } = useNativeSearchParams();
    const lensFoldersList = selectedOrganizationV3?.lensSpacesList ?? [];
    const [searchText, setSearchText] = useState("");

    const getSearchKey = useCallback((lensFolder: LensSpace) => `${lensFolder.name} ${lensFolder.id}`, []);

    const { data, onColumnSort } = useTableFilterAndSort({
        tableData: lensFoldersList,
        initialColumn: "name",
        searchText,
        getSearchKey,
    });

    const loadMoreData = useCallback((): Promise<unknown> => new Promise(() => null), []);

    const onClick = useCallback(
        (_: React.MouseEvent<HTMLDivElement>, idx: number) => {
            setNativeSearchParams(LensFoldersSearchParams.LensFolder, data[idx]?.id ?? "");
            handleView("lens");
        },
        [data, handleView, setNativeSearchParams],
    );

    return (
        <SearchableCard
            className={fullHeightCardCSS}
            searchPlaceholder="Search for a lens folder."
            padBody={false}
            onSearch={setSearchText}
        >
            <AutoSizer>
                {({ height, width }) => (
                    <VirtualTable
                        isLoading={false}
                        data={[...data]}
                        columns={getColumns()}
                        width={width}
                        height={height}
                        hasMore={false}
                        rowHeight={60}
                        emptyMessage={"No Folders found"}
                        loadMore={loadMoreData}
                        onColumnSort={onColumnSort}
                        onRowClick={onClick}
                        rowClassName={rowCSS}
                    />
                )}
            </AutoSizer>
        </SearchableCard>
    );
};
